import { useState, useCallback, useRef } from 'react'
import { Upload, X, FileBox, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  uploadAircraftModel,
  getModelPublicUrl,
  deleteAsset,
  validateFileSize,
  formatFileSize,
  ASSET_FOLDERS,
} from '@/lib/supabase/storage'

interface ModelUploadProps {
  /** Current model path (if any) */
  value?: string
  /** Callback when model is uploaded or cleared */
  onChange: (path: string) => void
  /** Whether the upload is disabled */
  disabled?: boolean
  /** Maximum file size in MB (default: 50) */
  maxSizeMB?: number
  /** Custom class name for the container */
  className?: string
}

type UploadState = 'idle' | 'dragging' | 'uploading' | 'success' | 'error'

const ACCEPTED_EXTENSIONS = ['.glb', '.gltf']
const ACCEPTED_MIME_TYPES = ['model/gltf-binary', 'model/gltf+json', 'application/octet-stream']

export function ModelUpload({
  value,
  onChange,
  disabled = false,
  maxSizeMB = 50,
  className,
}: ModelUploadProps) {
  const [uploadState, setUploadState] = useState<UploadState>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [uploadProgress, setUploadProgress] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  /**
   * Validate file type and size
   */
  const validateFile = useCallback(
    (file: File): string | null => {
      // Check extension
      const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'))
      if (!ACCEPTED_EXTENSIONS.includes(extension)) {
        return `Invalid file type. Only ${ACCEPTED_EXTENSIONS.join(', ')} files are allowed.`
      }

      // Check size
      if (!validateFileSize(file, maxSizeMB)) {
        return `File too large. Maximum size is ${maxSizeMB}MB.`
      }

      return null
    },
    [maxSizeMB]
  )

  /**
   * Handle file upload
   */
  const handleUpload = useCallback(
    async (file: File) => {
      // Validate file
      const validationError = validateFile(file)
      if (validationError) {
        setErrorMessage(validationError)
        setUploadState('error')
        return
      }

      setUploadState('uploading')
      setErrorMessage(null)
      setUploadProgress(`Uploading ${file.name} (${formatFileSize(file.size)})...`)

      try {
        const result = await uploadAircraftModel(file)

        // Store path relative to models folder for database
        const modelPath = `${ASSET_FOLDERS.MODELS}/${result.path.split('/').pop()}`
        onChange(modelPath)

        setUploadState('success')
        setUploadProgress(null)

        // Reset to idle after showing success
        setTimeout(() => setUploadState('idle'), 2000)
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Upload failed'
        setErrorMessage(message)
        setUploadState('error')
        setUploadProgress(null)
      }
    },
    [validateFile, onChange]
  )

  /**
   * Handle file input change
   */
  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file) {
        handleUpload(file)
      }
      // Reset input so same file can be selected again
      event.target.value = ''
    },
    [handleUpload]
  )

  /**
   * Handle drag events
   */
  const handleDragEnter = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      event.stopPropagation()
      if (!disabled) {
        setUploadState('dragging')
      }
    },
    [disabled]
  )

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setUploadState('idle')
  }, [])

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      event.stopPropagation()
      setUploadState('idle')

      if (disabled) return

      const file = event.dataTransfer.files?.[0]
      if (file) {
        handleUpload(file)
      }
    },
    [disabled, handleUpload]
  )

  /**
   * Open file picker
   */
  const handleClick = useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click()
    }
  }, [disabled])

  /**
   * Clear current model
   */
  const handleClear = useCallback(async () => {
    if (value) {
      try {
        // Try to delete from storage (may fail if file doesn't exist)
        await deleteAsset(value)
      } catch {
        // Ignore deletion errors - file might not exist in storage
      }
      onChange('')
    }
  }, [value, onChange])

  /**
   * Get display filename from path
   */
  const getDisplayFilename = (path: string): string => {
    return path.split('/').pop() || path
  }

  /**
   * Get the public URL for the current model
   */
  const getPublicUrl = (path: string): string | null => {
    if (!path) return null
    try {
      return getModelPublicUrl(path)
    } catch {
      return null
    }
  }

  return (
    <div className={cn('space-y-2', className)}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={[...ACCEPTED_EXTENSIONS, ...ACCEPTED_MIME_TYPES].join(',')}
        onChange={handleFileChange}
        className="hidden"
        disabled={disabled}
      />

      {/* Current model display */}
      {value && uploadState !== 'uploading' && (
        <div className="flex items-center gap-2 p-3 rounded-md border bg-muted/50">
          <FileBox className="h-5 w-5 text-primary shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">
              {getDisplayFilename(value)}
            </p>
            {getPublicUrl(value) && (
              <p className="text-xs text-muted-foreground truncate">
                {getPublicUrl(value)}
              </p>
            )}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={handleClear}
            disabled={disabled}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Remove model</span>
          </Button>
        </div>
      )}

      {/* Upload zone */}
      <div
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={cn(
          'relative border-2 border-dashed rounded-lg p-6 transition-colors cursor-pointer',
          'hover:border-primary/50 hover:bg-muted/50',
          uploadState === 'dragging' && 'border-primary bg-primary/5',
          uploadState === 'uploading' && 'border-muted bg-muted/30 cursor-wait',
          uploadState === 'success' && 'border-green-500 bg-green-500/5',
          uploadState === 'error' && 'border-destructive bg-destructive/5',
          disabled && 'opacity-50 cursor-not-allowed hover:border-border hover:bg-transparent'
        )}
      >
        <div className="flex flex-col items-center gap-2 text-center">
          {/* Icon based on state */}
          {uploadState === 'uploading' && (
            <Loader2 className="h-8 w-8 text-primary animate-spin" />
          )}
          {uploadState === 'success' && (
            <CheckCircle2 className="h-8 w-8 text-green-500" />
          )}
          {uploadState === 'error' && (
            <AlertCircle className="h-8 w-8 text-destructive" />
          )}
          {(uploadState === 'idle' || uploadState === 'dragging') && (
            <Upload
              className={cn(
                'h-8 w-8',
                uploadState === 'dragging' ? 'text-primary' : 'text-muted-foreground'
              )}
            />
          )}

          {/* Text based on state */}
          {uploadState === 'uploading' && (
            <p className="text-sm text-muted-foreground">{uploadProgress}</p>
          )}
          {uploadState === 'success' && (
            <p className="text-sm text-green-600">Upload complete!</p>
          )}
          {uploadState === 'error' && (
            <p className="text-sm text-destructive">{errorMessage}</p>
          )}
          {(uploadState === 'idle' || uploadState === 'dragging') && (
            <>
              <p className="text-sm font-medium">
                {value ? 'Replace 3D model' : 'Upload 3D model'}
              </p>
              <p className="text-xs text-muted-foreground">
                Drag & drop or click to select a GLB/GLTF file (max {maxSizeMB}MB)
              </p>
            </>
          )}
        </div>
      </div>

      {/* Error message (shown below for persistent errors) */}
      {uploadState === 'error' && errorMessage && (
        <p className="text-xs text-destructive flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          {errorMessage}
        </p>
      )}
    </div>
  )
}
